import React from 'react'

/**
 * Small pieces shared by the tenant's site pages (sites list, request a site,
 * park a domain, add a subdomain).
 *
 * Errors: the user sees the server's `detail` verbatim (fetchAPI keeps it, and
 * replaces raw browser/runtime messages with a clean one). The raw error goes
 * to the dev console before it is swallowed (CLAUDE.md §9).
 */

export function toMessage(context: string, err: unknown, fallback: string): string {
  // eslint-disable-next-line no-console
  console.error(`[sites] ${context}`, err)
  return err instanceof Error && err.message ? err.message : fallback
}

export const ErrorBox: React.FC<{ message: string | null; testId: string }> = ({ message, testId }) => {
  if (!message) return null
  return (
    <div
      role="alert"
      className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3"
    >
      <p data-testid={testId}>{message}</p>
    </div>
  )
}

/** A single-action confirmation: no fields, just Yes / No (CLAUDE.md §15). */
export const ConfirmModal: React.FC<{
  testId: string
  title: string
  children: React.ReactNode
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}> = ({ testId, title, children, confirmLabel, busy, onConfirm, onCancel }) => (
  <div
    data-testid={testId}
    role="dialog"
    aria-modal="true"
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  >
    <div className="bg-white dark:bg-gray-800 rounded-md p-6 w-full max-w-md space-y-4">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
      <div className="text-sm text-gray-700 dark:text-gray-300">{children}</div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid={`${testId}-no`}
          onClick={onCancel}
          disabled={busy}
          className="btn-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid={`${testId}-yes`}
          onClick={onConfirm}
          disabled={busy}
          className="btn-primary"
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
)

export const inputClass = 'mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600'
export const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300'
export const cardClass =
  'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6'
